export type RenderQuality = 'high' | 'low' | 'unsupported';

export interface GraphicsCapabilities {
  hasWebGl: boolean;
  /** The browser would only give WebGL through a slow path (`failIfMajorPerformanceCaveat`). */
  hasMajorPerformanceCaveat: boolean;
  /** `navigator.deviceMemory` in GB, where the browser says. */
  deviceMemoryGb?: number;
  logicalCores?: number;
  /** The unmasked renderer string, e.g. "ANGLE (Intel, Intel(R) UHD Graphics 620 …)". */
  gpuRenderer?: string;
}

/** Renderers that draw on the CPU: remote desktops, VMs and machines with no graphics driver. */
const SOFTWARE_RENDERERS = /swiftshader|llvmpipe|softpipe|software|microsoft basic render/i;
const LOW_MEMORY_GB = 4;
const FEW_CORES = 2;

/**
 * How hard the 3D vehicle may work on this device. An office desktop with 4 GB
 * and built-in graphics gets the low tier (no real-time shadows, single pixel
 * ratio, plainer wheels) rather than a stuttering high one; a browser with no
 * WebGL gets the plan chart instead.
 */
export function chooseRenderQuality(capabilities: GraphicsCapabilities): RenderQuality {
  if (!capabilities.hasWebGl) return 'unsupported';
  if (capabilities.hasMajorPerformanceCaveat) return 'low';
  if (capabilities.gpuRenderer && SOFTWARE_RENDERERS.test(capabilities.gpuRenderer)) return 'low';
  if (capabilities.deviceMemoryGb !== undefined && capabilities.deviceMemoryGb <= LOW_MEMORY_GB) return 'low';
  if (capabilities.logicalCores !== undefined && capabilities.logicalCores <= FEW_CORES) return 'low';
  return 'high';
}

const SLOW_FRAME_MS = 40;
const FRAMES_SAMPLED = 30;

/**
 * Whether the frames drawn while the viewer was moving the camera say this
 * device can't keep up: the median of the last 30 over 40 ms (under 25 fps).
 * Judged on the median so one hitch, a tab switch or a GC pause, doesn't
 * count.
 */
export function shouldStepDownQuality(frameTimesMs: number[]): boolean {
  if (frameTimesMs.length < FRAMES_SAMPLED) return false;
  const recent = [...frameTimesMs.slice(-FRAMES_SAMPLED)].sort((a, b) => a - b);
  return recent[Math.floor(recent.length / 2)] > SLOW_FRAME_MS;
}
